import { useState } from "react";
import PropTypes from "prop-types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import ShareDialog from "./ShareDialog";
import { VISIBILITY_LABELS } from "./visibility";
import { displayName } from "../tasks/userDisplay";

/**
 * Who something reaches (household sharing D1/D2) — private, household, or
 * specific people — for anything shared those three ways (drawings,
 * snippets). A select for whoever may change it; a badge naming the owner
 * for everyone else. "Specific people…" opens the Share dialog (nothing
 * changes until Share); leaving it asks first, because the list is
 * forgotten. Only `canChangeSharing` (the owner's person) touches people.
 *
 * `onChange(changes)` saves a plain visibility flip (the caller reports
 * failures); `onShare(changes)` saves the dialog and must throw on failure,
 * so the dialog can show why.
 */
function SharingControl({
  title,
  selectLabel,
  visibility,
  sharedWith,
  ownerName,
  canChangeVisibility,
  canChangeSharing,
  hints,
  householdOption,
  shareDescription,
  idPrefix,
  onChange,
  onShare,
}) {
  const [sharing, setSharing] = useState(false);
  const [unsharingTo, setUnsharingTo] = useState(null);
  const people = sharedWith || [];

  return (
    <>
      {canChangeVisibility && (visibility !== "people" || canChangeSharing) ? (
        <NativeSelect
          value={visibility}
          onChange={(event) => {
            const next = event.target.value;
            // "Specific people" needs a list first: the dialog sets both
            if (next === "people") setSharing(true);
            else if (visibility === "people") setUnsharingTo(next);
            else onChange({ visibility: next });
          }}
          aria-label={selectLabel}
          title={hints[visibility]}
          className="h-8 w-auto"
        >
          <option value="private">
            {canChangeSharing
              ? "Private — you and your agents"
              : "Private — its owner and their agents"}
          </option>
          <option value="household">{householdOption}</option>
          {canChangeSharing && <option value="people">Specific people…</option>}
        </NativeSelect>
      ) : (
        <Badge variant="secondary" title={hints[visibility]}>
          {VISIBILITY_LABELS[visibility]} · {ownerName}&apos;s
        </Badge>
      )}
      {visibility === "people" && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSharing(true)}
          disabled={!canChangeSharing}
          title={people.map(displayName).join(", ")}
          aria-label={
            canChangeSharing
              ? `Shared with ${people.length} — change who`
              : `Shared with ${people.map(displayName).join(", ")}`
          }
        >
          Shared with {people.length}
        </Button>
      )}

      {canChangeSharing && (
        <ShareDialog
          open={sharing}
          title={title}
          description={shareDescription}
          sharedWith={sharedWith}
          idPrefix={idPrefix}
          onClose={() => setSharing(false)}
          onSave={onShare}
        />
      )}
      <AlertDialog
        open={unsharingTo !== null}
        onOpenChange={(open) => !open && setUnsharingTo(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Stop sharing with {people.length}{" "}
              {people.length === 1 ? "person" : "people"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {unsharingTo === "household"
                ? "It goes to your household instead, and the list is forgotten: choosing Specific people later starts from nobody."
                : "It becomes private to you and your agents, and the list is forgotten: choosing Specific people later starts from nobody."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep sharing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const next = unsharingTo;
                setUnsharingTo(null);
                onChange({ visibility: next });
              }}
            >
              {unsharingTo === "household"
                ? "Make it household"
                : "Make it private"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

SharingControl.propTypes = {
  title: PropTypes.string.isRequired,
  selectLabel: PropTypes.string.isRequired,
  visibility: PropTypes.oneOf(["private", "household", "people"]).isRequired,
  sharedWith: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, username: PropTypes.string }),
  ),
  ownerName: PropTypes.string,
  canChangeVisibility: PropTypes.bool,
  canChangeSharing: PropTypes.bool,
  hints: PropTypes.objectOf(PropTypes.string).isRequired,
  householdOption: PropTypes.string.isRequired,
  shareDescription: PropTypes.string.isRequired,
  idPrefix: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
};

export default SharingControl;
